import { useCallback, useMemo } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import moment from 'moment'

import useLocalStorage from 'modules/core/lib/useLocalStorage'

interface StoredDateRange {
  startDate: string
  endDate: string
}

const getDefaultRange = (): StoredDateRange => ({
  startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
})

export const useTreasuryDateRange = () => {
  const [stored, setStored] = useLocalStorage<StoredDateRange>(
    LOCAL_STORAGE_KEYS.treasuryDateRange,
    getDefaultRange()
  )

  const range = stored ?? getDefaultRange()

  // Memoize Date objects to keep stable references across renders.
  // Without this, new Date() creates different object references every render,
  // causing downstream useMemo deps to always appear "changed" → infinite loop.
  // Use moment to parse as local date (not UTC) to preserve the intended day.
  const startDate = useMemo(() => moment(range.startDate, 'YYYY-MM-DD').toDate(), [range.startDate])
  const endDate = useMemo(() => moment(range.endDate, 'YYYY-MM-DD').toDate(), [range.endDate])

  const onDateRangeChange = useCallback(
    (newStartDate: Date, newEndDate: Date) => {
      setStored({
        startDate: moment(newStartDate).format('YYYY-MM-DD'),
        endDate: moment(newEndDate).format('YYYY-MM-DD'),
      })
    },
    [setStored]
  )

  return {
    startDate,
    endDate,
    onDateRangeChange,
  }
}
